import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
    Trash2, Pause, Play, Loader2, Search, Mail, Calendar,
    CheckCircle2, AlertCircle, Clock, Filter, ArrowLeft, RefreshCw, Layers, Eye
} from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { format, addDays } from "date-fns";
import { Calendar as PrimeCalendar } from "primereact/calendar";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { API_URL, getAuthHeader } from "@/config";
import { Link } from "react-router-dom";

interface ScheduledEmail {
    id: number;
    virtual_class_id: string;
    sender_email: string;
    recipient_email: string;
    subject: string;
    body: string;
    scheduled_at: string;
    sent_at: string | null;
    status: 'pending' | 'sent' | 'failed' | 'paused';
    error_message: string | null;
    virtual_class?: {
        name: string;
    };
}

const ScheduledEmails: React.FC = () => {
    const { token } = useAuth();
    const [emails, setEmails] = useState<ScheduledEmail[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("all");

    // Date Range State
    const [dateRange, setDateRange] = useState<(Date | null)[]>([
        new Date(),
        addDays(new Date(), 10)
    ]);

    const [filters, setFilters] = useState({
        sender_email: "",
        recipient_email: "",
        subject: "",
        virtual_class_id: ""
    });

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: keyof ScheduledEmail | 'virtual_class.name'; direction: 'asc' | 'desc' } | null>(null);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [isBulkActionLoading, setIsBulkActionLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        total: 0
    });
    const [pageSize, setPageSize] = useState(50);
    const [isSelectingAll, setIsSelectingAll] = useState(false);
    const [previewEmail, setPreviewEmail] = useState<ScheduledEmail | null>(null);
    const [classes, setClasses] = useState<{ id: number, name: string }[]>([]);

    const fetchClasses = async () => {
        if (!token) return;
        try {
            const response = await fetch(`${API_URL}/virtual-classes`, {
                headers: getAuthHeader(token),
            });
            if (response.ok) {
                const data = await response.json();
                setClasses(data);
            }
        } catch (error) {
            console.error("Failed to fetch classes", error);
        }
    };

    useEffect(() => {
        fetchClasses();
    }, [token]);

    const fetchEmails = async () => {
        if (!token) return;
        setIsLoading(true);
        try {
            const queryParams = new URLSearchParams({
                no_pagination: 'true',
                start_date: dateRange[0] ? format(dateRange[0], 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
                end_date: dateRange[1] ? format(dateRange[1], 'yyyy-MM-dd') : format(addDays(new Date(), 10), 'yyyy-MM-dd')
            });

            if (statusFilter !== "all") queryParams.append('status', statusFilter);
            if (debouncedSearchTerm) queryParams.append('search', debouncedSearchTerm);

            if (filters.sender_email) queryParams.append('sender_email', filters.sender_email);
            if (filters.recipient_email) queryParams.append('recipient_email', filters.recipient_email);
            if (filters.subject) queryParams.append('subject', filters.subject);
            if (filters.virtual_class_id && filters.virtual_class_id !== "all") queryParams.append('virtual_class_id', filters.virtual_class_id);

            const response = await fetch(`${API_URL}/scheduled-emails?${queryParams.toString()}`, {
                headers: getAuthHeader(token),
            });
            if (response.ok) {
                const data = await response.json();
                // Handle both paginated and non-paginated responses just in case, though we requested no_pagination
                const emailsData = data.data || [];
                setEmails(emailsData);
                // Reset pagination to page 1 when new data comes in
                setPagination(prev => ({ ...prev, current_page: 1, total: emailsData.length }));
            }
        } catch (error) {
            toast.error("Erreur lors de la récupération des emails");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchEmails();
    }, [statusFilter, debouncedSearchTerm, filters, dateRange]);

    const handleAction = async (id: number, action: 'pause' | 'resume' | 'retry' | 'delete') => {
        if (!token) return;

        const method = action === 'delete' ? 'DELETE' : 'POST';
        const url = `${API_URL}/scheduled-emails/${id}${action === 'delete' ? '' : '/' + action}`;

        try {
            const response = await fetch(url, {
                method,
                headers: getAuthHeader(token),
            });

            if (response.ok) {
                const messages = {
                    delete: "Email supprimé",
                    pause: "Email mis en pause",
                    resume: "Email activé",
                    retry: "Email reprogrammé pour envoi immédiat"
                };
                toast.success(messages[action]);

                // Optimistic update
                if (action === 'delete') {
                    setEmails(prev => prev.filter(e => e.id !== id));
                    setPagination(prev => ({ ...prev, total: prev.total - 1 }));
                } else {
                    setEmails(prev => prev.map(e => {
                        if (e.id === id) {
                            return {
                                ...e,
                                status: action === 'pause' ? 'paused' : 'pending',
                                scheduled_at: action === 'retry' ? new Date().toISOString() : e.scheduled_at,
                                error_message: action === 'retry' ? null : e.error_message
                            };
                        }
                        return e;
                    }));
                }
            } else {
                toast.error("Une erreur est survenue");
            }
        } catch (error) {
            toast.error("Erreur lors de l'action");
        }
    };

    const handleBulkAction = async (action: 'pause' | 'resume' | 'retry' | 'delete') => {
        if (!token || selectedIds.length === 0) return;

        if (action === 'delete' && !window.confirm(`Voulez-vous vraiment supprimer ${selectedIds.length} email(s) ?`)) {
            return;
        }

        setIsBulkActionLoading(true);
        try {
            const response = await fetch(`${API_URL}/scheduled-emails/bulk-action`, {
                method: 'POST',
                headers: getAuthHeader(token),
                body: JSON.stringify({ ids: selectedIds, action })
            });

            if (response.ok) {
                const data = await response.json();
                toast.success(data.message);
                toast.success(data.message);
                setSelectedIds([]);
                fetchEmails();
            } else {
                toast.error("Erreur lors de l'action groupée");
            }
        } catch (error) {
            toast.error("Erreur de connexion");
        } finally {
            setIsBulkActionLoading(false);
        }
    };

    // Sorting and Logic for Display
    const handleSort = (key: keyof ScheduledEmail | 'virtual_class.name') => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedEmails = React.useMemo(() => {
        let sortableEmails = [...emails];
        if (sortConfig !== null) {
            sortableEmails.sort((a, b) => {
                let aValue: any = a[sortConfig.key as keyof ScheduledEmail];
                let bValue: any = b[sortConfig.key as keyof ScheduledEmail];

                if (sortConfig.key === 'virtual_class.name') {
                    aValue = a.virtual_class?.name || '';
                    bValue = b.virtual_class?.name || '';
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableEmails;
    }, [emails, sortConfig]);

    // Client-side pagination logic
    const totalPages = Math.ceil(sortedEmails.length / pageSize);
    const paginatedEmails = sortedEmails.slice(
        (pagination.current_page - 1) * pageSize,
        pagination.current_page * pageSize
    );

    // Update total count when emails change
    useEffect(() => {
        setPagination(prev => ({ ...prev, total: emails.length, last_page: Math.ceil(emails.length / pageSize) }));
    }, [emails, pageSize]);

    const toggleSelectAll = () => {
        if (selectedIds.length === emails.length && emails.length > 0) {
            setSelectedIds([]);
        } else {
            setSelectedIds(emails.map(e => e.id));
        }
    };

    const toggleSelect = (id: number) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(prev => prev.filter(i => i !== id));
        } else {
            setSelectedIds(prev => [...prev, id]);
        }
    };

    // Derived state for bulk actions
    const filteredEmails = sortedEmails; // Alias for compatibility with previous code structure
    const selectedEmailsList = emails.filter(e => selectedIds.includes(e.id));
    const allPending = selectedEmailsList.length > 0 && selectedEmailsList.every(e => e.status === 'pending');
    const allPaused = selectedEmailsList.length > 0 && selectedEmailsList.every(e => e.status === 'paused');
    const allFailed = selectedEmailsList.length > 0 && selectedEmailsList.every(e => e.status === 'failed');
    const canDelete = selectedEmailsList.length > 0 && selectedEmailsList.every(e => !['sent', 'processing'].includes(e.status));

    const getStatusBadge = (email: ScheduledEmail) => {
        switch (email.status) {
            case 'sent':
                return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-none font-bold flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3" /> Envoyé</Badge>;
            case 'failed':
                return (
                    <div className="flex flex-col items-center gap-1">
                        <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-none font-bold flex items-center gap-1.5" title={email.error_message || "Erreur inconnue"}>
                            <AlertCircle className="h-3 w-3" /> Échec
                        </Badge>
                        {email.error_message && (
                            <span className="text-[9px] font-bold text-red-400 capitalize tracking-tighter truncate max-w-[100px]" title={email.error_message}>
                                {email.error_message}
                            </span>
                        )}
                    </div>
                );
            case 'paused':
                return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-none font-bold flex items-center gap-1.5"><Pause className="h-3 w-3" /> Suspendu</Badge>;
            default:
                return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-none font-bold flex items-center gap-1.5"><Clock className="h-3 w-3" /> En attente</Badge>;
        }
    };

    return (
        <div className="h-screen flex flex-col py-6 px-4 space-y-6 animate-in fade-in duration-700 overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-1">
                    <div className="flex items-center gap-3">
                        <Link to="/virtual-classes">
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-slate-100">
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                        </Link>
                        <div className="flex items-baseline gap-3">
                            <h1 className="text-2xl font-black tracking-tight text-slate-900 capitalize">Emails Programmés</h1>
                        </div>
                    </div>
                    <p className="text-slate-500 font-medium ml-11">
                        Du <span className="font-bold text-slate-700">{dateRange[0] ? format(dateRange[0], 'dd/MM/yyyy') : '—'}</span> au <span className="font-bold text-slate-700">{dateRange[1] ? format(dateRange[1], 'dd/MM/yyyy') : '—'}</span>
                    </p>
                </div>
            </div>

            {
                selectedIds.length > 0 && (
                    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white p-2 pl-6 pr-2 rounded-full shadow-2xl flex items-center gap-6 animate-in slide-in-from-bottom-6 duration-300 border border-slate-700/50 backdrop-blur-md">
                        <span className="text-sm font-bold flex items-center gap-2">
                            <div className="bg-primary text-white text-[10px] font-black h-5 w-5 rounded-full flex items-center justify-center">
                                {selectedIds.length}
                            </div>
                            sélectionné(s)
                        </span>
                        <div className="h-6 w-[1px] bg-slate-700"></div>
                        <div className="flex items-center gap-1">
                            {allPending && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleBulkAction('pause')}
                                    className="text-amber-400 hover:text-amber-300 hover:bg-white/10 h-8 px-3 rounded-xl font-bold text-xs"
                                    disabled={isBulkActionLoading}
                                >
                                    <Pause className="h-3.5 w-3.5 mr-2" /> Pause
                                </Button>
                            )}
                            {allPaused && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleBulkAction('resume')}
                                    className="text-emerald-400 hover:text-emerald-300 hover:bg-white/10 h-8 px-3 rounded-xl font-bold text-xs"
                                    disabled={isBulkActionLoading}
                                >
                                    <Play className="h-3.5 w-3.5 mr-2" /> Reprendre
                                </Button>
                            )}
                            {allFailed && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleBulkAction('retry')}
                                    className="text-blue-400 hover:text-blue-300 hover:bg-white/10 h-8 px-3 rounded-xl font-bold text-xs"
                                    disabled={isBulkActionLoading}
                                >
                                    <RefreshCw className="h-3.5 w-3.5 mr-2" /> Réessayer
                                </Button>
                            )}
                            {canDelete && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleBulkAction('delete')}
                                    className="text-red-400 hover:text-red-300 hover:bg-white/10 h-8 px-3 rounded-xl font-bold text-xs"
                                    disabled={isBulkActionLoading}
                                >
                                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Supprimer
                                </Button>
                            )}
                        </div>
                    </div>
                )
            }

            <Card className="flex-1 min-h-0 flex flex-col border-none shadow-2xl shadow-slate-200/50 bg-white/80 backdrop-blur-xl rounded-[2.5rem] overflow-hidden">
                <CardHeader className="py-0 space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 bg-slate-50 p-1.5 rounded-2xl border border-slate-100">
                            {[
                                { id: 'all', label: 'Tous' },
                                { id: 'pending', label: 'En attente' },
                                { id: 'paused', label: 'Suspendus' },
                                { id: 'sent', label: 'Envoyés' },
                                { id: 'failed', label: 'Échecs' }
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setStatusFilter(tab.id)}
                                    className={`px-4 py-2 text-xs font-bold rounded-xl transition-all ${statusFilter === tab.id
                                        ? 'bg-white text-primary shadow-sm ring-1 ring-slate-200'
                                        : 'text-slate-500 hover:text-slate-700'
                                        }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                            {emails.length} email{emails.length !== 1 ? 's' : ''}
                        </p>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="relative flex-1 max-w-md group">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-primary transition-colors" />
                            <Input
                                placeholder="Rechercher par email, classe ou objet..."
                                className="pl-12 h-12 bg-slate-50 border-slate-100 rounded-2xl focus:ring-primary/20 focus:border-primary/30 transition-all text-sm font-medium"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2 bg-slate-50 p-1.5 rounded-2xl border border-slate-100 px-3">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Période</span>
                                <PrimeCalendar
                                    value={dateRange}
                                    onChange={(e) => e.value && setDateRange(e.value as (Date | null)[])}
                                    selectionMode="range"
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    readOnlyInput
                                    numberOfMonths={2}
                                    className="w-[260px]"
                                    inputClassName="p-inputtext w-full h-9 bg-transparent border-none shadow-none text-xs font-bold px-2 focus:ring-0"
                                />
                            </div>
                            <Button
                                variant="outline"
                                onClick={() => setShowFilters(!showFilters)}
                                className={`h-12 px-4 rounded-2xl border-slate-200 font-bold ${showFilters ? 'bg-slate-100 text-primary border-primary/20' : 'text-slate-500'}`}
                            >
                                <Filter className="h-4 w-4 mr-2" />
                                Filtres avancés
                                {(filters.sender_email || filters.recipient_email || filters.subject) && (
                                    <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center bg-primary text-white rounded-full text-[10px]">
                                        {[filters.sender_email, filters.recipient_email, filters.subject, filters.virtual_class_id].filter(val => val && val !== "all").length}
                                    </Badge>
                                )}
                            </Button>
                        </div>
                    </div>
                </CardHeader>

                {showFilters && (
                    <div className="px-8 pb-8 animate-in slide-in-from-top-2">
                        <div className="bg-slate-50/50 rounded-2xl p-6 border border-slate-100 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase">Classe Virtuelle</label>
                                <Select
                                    value={filters.virtual_class_id}
                                    onValueChange={(value) => setFilters(prev => ({ ...prev, virtual_class_id: value }))}
                                >
                                    <SelectTrigger className="bg-white border-slate-200 rounded-xl">
                                        <SelectValue placeholder="Toutes les classes" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Toutes les classes</SelectItem>
                                        {classes.map((cls) => (
                                            <SelectItem key={cls.id} value={String(cls.id)}>{cls.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase">Expéditeur</label>
                                <Input
                                    placeholder="Ex: pierre@amc.com"
                                    value={filters.sender_email}
                                    onChange={(e) => setFilters(prev => ({ ...prev, sender_email: e.target.value }))}
                                    className="bg-white border-slate-200 rounded-xl"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase">Destinataire</label>
                                <Input
                                    placeholder="Ex: jean.dupont@gmail.com"
                                    value={filters.recipient_email}
                                    onChange={(e) => setFilters(prev => ({ ...prev, recipient_email: e.target.value }))}
                                    className="bg-white border-slate-200 rounded-xl"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase">Sujet de l'email</label>
                                <Input
                                    placeholder="Contient..."
                                    value={filters.subject}
                                    onChange={(e) => setFilters(prev => ({ ...prev, subject: e.target.value }))}
                                    className="bg-white border-slate-200 rounded-xl"
                                />
                            </div>
                        </div>
                    </div>
                )}

                <CardContent className="p-0 flex-1 flex flex-col min-h-0 overflow-hidden">
                    <div className="flex-1 overflow-auto relative">
                        <Table>
                            <TableHeader className="sticky top-0 bg-white/95 backdrop-blur-sm z-10">
                                <TableRow className="border-slate-50 hover:bg-transparent">
                                    <TableHead className="w-[50px] pl-6">
                                        <Checkbox
                                            checked={selectedIds.length === pagination.total && pagination.total > 0}
                                            onCheckedChange={toggleSelectAll}
                                            className="border-slate-300 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                                        />
                                    </TableHead>
                                    <TableHead className="w-[40px] text-[11px] font-black capitalize tracking-widest text-slate-400">#</TableHead>
                                    <TableHead
                                        className="w-[250px] text-[11px] font-black capitalize tracking-widest text-slate-400 cursor-pointer hover:text-slate-600 transition-colors"
                                        onClick={() => handleSort('recipient_email')}
                                    >
                                        Destinataire / Classe {sortConfig?.key === 'recipient_email' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                    </TableHead>
                                    <TableHead
                                        className="text-[11px] font-black capitalize tracking-widest text-slate-400 cursor-pointer hover:text-slate-600 transition-colors"
                                        onClick={() => handleSort('subject')}
                                    >
                                        Objet de l'email {sortConfig?.key === 'subject' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                    </TableHead>
                                    <TableHead
                                        className="text-[11px] font-black capitalize tracking-widest text-slate-400 cursor-pointer hover:text-slate-600 transition-colors"
                                        onClick={() => handleSort('scheduled_at')}
                                    >
                                        Date de programmation {sortConfig?.key === 'scheduled_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                    </TableHead>
                                    <TableHead className="text-[11px] font-black capitalize tracking-widest text-slate-400 text-center">Statut</TableHead>
                                    <TableHead className="w-[120px] pr-8 text-right text-[11px] font-black capitalize tracking-widest text-slate-400">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="h-64 text-center">
                                            <div className="flex flex-col items-center justify-center gap-3">
                                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                                <p className="text-sm font-bold text-slate-400 italic">Chargement des emails...</p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : paginatedEmails.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="h-64 text-center">
                                            <div className="flex flex-col items-center justify-center gap-4">
                                                <div className="h-16 w-16 bg-slate-50 rounded-3xl flex items-center justify-center">
                                                    <Mail className="h-8 w-8 text-slate-200" />
                                                </div>
                                                <p className="text-sm font-bold text-slate-400 italic">Aucun email trouvé</p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedEmails.map((email, index) => (
                                        <TableRow key={email.id} className={`group border-slate-50 transition-colors ${selectedIds.includes(email.id) ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-slate-50/50'}`}>
                                            <TableCell className="pl-6 py-5">
                                                <Checkbox
                                                    checked={selectedIds.includes(email.id)}
                                                    onCheckedChange={() => toggleSelect(email.id)}
                                                    className="border-slate-300 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                                                />
                                            </TableCell>
                                            <TableCell className="text-[10px] font-bold text-slate-400">
                                                {((pagination.current_page - 1) * pageSize) + index + 1}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-black text-slate-900 truncate max-w-[200px]">{email.recipient_email}</span>
                                                    <span className="text-[10px] font-bold text-primary capitalize bg-primary/5 px-2 py-0.5 rounded-lg w-fit mt-1.5">
                                                        {email.virtual_class?.name || "Classe inconnue"}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm font-medium text-slate-600 line-clamp-1">{email.subject}</span>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-2">
                                                    <div className="flex items-center gap-1.5 text-slate-700 text-xs font-bold">
                                                        <Calendar className="h-3.5 w-3.5 text-slate-400" />
                                                        {new Date(email.scheduled_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                                                    </div>
                                                    <div className="flex flex-col gap-1 mt-1">
                                                        <div className="flex items-center gap-2" title="Votre heure locale">
                                                            <Clock className="h-3 w-3 text-slate-400" />
                                                            <span className="text-[11px] font-bold text-slate-600">
                                                                {new Date(email.scheduled_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                            <span className="text-[9px] font-medium text-slate-400 bg-slate-100 px-1.5 rounded text-center">Local</span>
                                                        </div>
                                                        <div className="flex items-center gap-2" title="Heure de Paris">
                                                            <span className="text-[10px] w-3 flex justify-center">🇫🇷</span>
                                                            <span className="text-[11px] font-bold text-indigo-600">
                                                                {new Date(email.scheduled_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })}
                                                            </span>
                                                            <span className="text-[9px] font-medium text-indigo-400 bg-indigo-50 px-1.5 rounded text-center">Paris</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <div className="flex justify-center">
                                                    {getStatusBadge(email)}
                                                </div>
                                            </TableCell>
                                            <TableCell className="pr-8 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    {email.status === 'pending' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-9 w-9 text-amber-500 hover:text-amber-600 hover:bg-amber-50 rounded-xl"
                                                            onClick={() => handleAction(email.id, 'pause')}
                                                            title="Mettre en pause"
                                                        >
                                                            <Pause className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    {email.status === 'paused' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-9 w-9 text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl"
                                                            onClick={() => handleAction(email.id, 'resume')}
                                                            title="Reprendre"
                                                        >
                                                            <Play className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    {email.status === 'failed' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-9 w-9 text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl"
                                                            onClick={() => handleAction(email.id, 'retry')}
                                                            title="Réessayer l'envoi"
                                                        >
                                                            <RefreshCw className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-9 w-9 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-xl"
                                                        onClick={() => setPreviewEmail(email)}
                                                        title="Prévisualiser"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                    {!['sent', 'processing'].includes(email.status) && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-9 w-9 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl"
                                                            onClick={() => {
                                                                if (window.confirm("Supprimer cet email programmé ?")) {
                                                                    handleAction(email.id, 'delete');
                                                                }
                                                            }}
                                                            title="Supprimer"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )))
                                }
                            </TableBody>
                        </Table>
                    </div>

                    {
                        pagination.last_page > 1 && (
                            <div className="p-8 pt-4 pb-0 border-t border-slate-50 flex items-center justify-between">
                                <p className="text-xs font-bold text-slate-400 capitalize tracking-widest hidden md:block">
                                    PAGE {pagination.current_page} SUR {pagination.last_page} — {pagination.total} EMAILS
                                </p>
                                <div className="flex items-center gap-2">
                                    <Select
                                        value={pageSize.toString()}
                                        onValueChange={(val) => setPageSize(parseInt(val))}
                                    >
                                        <SelectTrigger className="h-9 w-[130px] rounded-xl border-slate-200 text-xs font-bold">
                                            <SelectValue placeholder="Par page" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="50">50 par page</SelectItem>
                                            <SelectItem value="100">100 par page</SelectItem>
                                            <SelectItem value="200">200 par page</SelectItem>
                                            <SelectItem value="500">500 par page</SelectItem>
                                            <SelectItem value="1000">1000 par page</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={pagination.current_page === 1}
                                            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                                            className="rounded-xl h-9 border-slate-200"
                                        >
                                            Précédent
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={pagination.current_page === totalPages}
                                            onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                                            className="rounded-xl h-9 border-slate-200"
                                        >
                                            Suivant
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                </CardContent>
            </Card>

            <Dialog open={!!previewEmail} onOpenChange={(open) => !open && setPreviewEmail(null)}>
                <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col gap-0 p-0 border-none shadow-2xl rounded-[2.5rem] bg-white ring-1 ring-slate-100">
                    <DialogHeader className="p-8 pb-6 border-b border-slate-50 bg-slate-50/30">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-primary/10 rounded-xl">
                                <Mail className="h-5 w-5 text-primary" />
                            </div>
                            <DialogTitle className="text-xl font-black text-slate-900 capitalize tracking-tight truncate">
                                {previewEmail?.subject}
                            </DialogTitle>
                        </div>
                        <DialogDescription className="flex flex-col gap-1">
                            <span className="flex items-center gap-2 text-xs font-bold text-slate-400 capitalize tracking-widest">
                                <span className="text-slate-500">POUR:</span> {previewEmail?.recipient_email}
                            </span>
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto p-8 bg-white min-h-[400px]">
                        {previewEmail?.body && (
                            <div
                                className="prose prose-slate max-w-none text-slate-600 font-medium leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: previewEmail.body }}
                            />
                        )}
                    </div>

                    <div className="p-6 bg-slate-50/50 border-t border-slate-50 flex justify-end">
                        <Button
                            variant="ghost"
                            onClick={() => setPreviewEmail(null)}
                            className="rounded-2xl h-11 px-8 font-black uppercase tracking-widest text-[10px] text-slate-400"
                        >
                            Fermer la prévisualisation
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ScheduledEmails;
